import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';
import {
  ComplianceSummary,
  AuditControl,
  AuditDomainLabel,
  AuditDomainWHOBlock,
} from '../../types';
import { auditService } from '../../services/auditService';

const useStyles = makeStyles(theme => ({
  overallCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    textAlign: 'center',
  },
  overallScore: {
    fontSize: '3rem',
    fontWeight: 'bold',
  },
  domainCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  domainName: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  progressLabel: {
    minWidth: 100,
    fontSize: '0.8rem',
  },
  progressBarWrapper: {
    flexGrow: 1,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  progressValue: {
    minWidth: 40,
    textAlign: 'right',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    marginRight: theme.spacing(2),
    fontSize: '0.8rem',
  },
  stackedBar: {
    display: 'flex',
    height: 24,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: theme.spacing(0.5),
  },
  barSegment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    transition: 'width 0.3s ease',
  },
  whoBlock: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
}));

export const ComplianceView = () => {
  const classes = useStyles();
  const [compliance, setCompliance] = useState<ComplianceSummary[]>([]);
  const [controls, setControls] = useState<AuditControl[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      auditService.getComplianceSummary(),
      auditService.getControls(),
    ]).then(([c, ctrl]) => {
      setCompliance(c);
      setControls(ctrl);
      setLoading(false);
    });
  }, []);

  if (loading) return <LinearProgress />;

  const totalControls = compliance.reduce((s, c) => s + c.totalControls, 0);
  const totalCompliant = compliance.reduce((s, c) => s + c.compliant, 0);
  const totalPartial = compliance.reduce((s, c) => s + c.partial, 0);
  const totalNonCompliant = compliance.reduce((s, c) => s + c.nonCompliant, 0);
  const overallPercent =
    totalControls > 0
      ? Math.round(
          ((totalCompliant + totalPartial * 0.5) / totalControls) * 100,
        )
      : 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Compliance Overview — WHO Digital Health Controls
      </Typography>

      {/* Overall compliance */}
      <Card className={classes.overallCard} variant="outlined">
        <Typography
          className={classes.overallScore}
          style={{
            color:
              overallPercent >= 60
                ? '#4caf50'
                : overallPercent >= 40
                ? '#ff9800'
                : '#f44336',
          }}
        >
          {overallPercent}%
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Overall Compliance Score
        </Typography>
        <Box mt={2}>
          <div
            className={classes.stackedBar}
            style={{ maxWidth: 500, margin: '0 auto' }}
          >
            <div
              className={classes.barSegment}
              style={{
                width: `${
                  totalControls > 0 ? (totalCompliant / totalControls) * 100 : 0
                }%`,
                backgroundColor: '#4caf50',
              }}
            >
              {totalCompliant}
            </div>
            <div
              className={classes.barSegment}
              style={{
                width: `${
                  totalControls > 0 ? (totalPartial / totalControls) * 100 : 0
                }%`,
                backgroundColor: '#ff9800',
              }}
            >
              {totalPartial}
            </div>
            <div
              className={classes.barSegment}
              style={{
                width: `${
                  totalControls > 0
                    ? (totalNonCompliant / totalControls) * 100
                    : 0
                }%`,
                backgroundColor: '#f44336',
              }}
            >
              {totalNonCompliant}
            </div>
          </div>
          <Box mt={1}>
            <span className={classes.legendItem}>
              <CheckCircleIcon style={{ fontSize: 14, color: '#4caf50' }} />{' '}
              Compliant ({totalCompliant})
            </span>
            <span className={classes.legendItem}>
              <WarningIcon style={{ fontSize: 14, color: '#ff9800' }} /> Partial
              ({totalPartial})
            </span>
            <span className={classes.legendItem}>
              <ErrorIcon style={{ fontSize: 14, color: '#f44336' }} />{' '}
              Non-Compliant ({totalNonCompliant})
            </span>
          </Box>
        </Box>
      </Card>

      {/* Per-domain compliance */}
      <Typography variant="h6" className={classes.sectionTitle}>
        Domain Compliance Breakdown
      </Typography>
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        {compliance.map(cs => (
          <Grid item xs={12} sm={6} md={3} key={cs.domain}>
            <Card className={classes.domainCard} variant="outlined">
              <Typography className={classes.domainName} variant="subtitle2">
                {AuditDomainLabel[cs.domain]}
              </Typography>
              <Typography className={classes.whoBlock}>
                {AuditDomainWHOBlock[cs.domain]}
              </Typography>

              <Box mt={1} mb={1}>
                <div className={classes.stackedBar}>
                  {cs.totalControls > 0 && (
                    <>
                      <div
                        className={classes.barSegment}
                        style={{
                          width: `${(cs.compliant / cs.totalControls) * 100}%`,
                          backgroundColor: '#4caf50',
                        }}
                      >
                        {cs.compliant > 0 ? cs.compliant : ''}
                      </div>
                      <div
                        className={classes.barSegment}
                        style={{
                          width: `${(cs.partial / cs.totalControls) * 100}%`,
                          backgroundColor: '#ff9800',
                        }}
                      >
                        {cs.partial > 0 ? cs.partial : ''}
                      </div>
                      <div
                        className={classes.barSegment}
                        style={{
                          width: `${
                            (cs.nonCompliant / cs.totalControls) * 100
                          }%`,
                          backgroundColor: '#f44336',
                        }}
                      >
                        {cs.nonCompliant > 0 ? cs.nonCompliant : ''}
                      </div>
                      {cs.notAssessed > 0 && (
                        <div
                          className={classes.barSegment}
                          style={{
                            width: `${
                              (cs.notAssessed / cs.totalControls) * 100
                            }%`,
                            backgroundColor: '#9e9e9e',
                          }}
                        >
                          {cs.notAssessed}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Box>

              <Typography
                variant="h5"
                style={{
                  fontWeight: 'bold',
                  color:
                    cs.compliancePercent >= 60
                      ? '#4caf50'
                      : cs.compliancePercent >= 40
                      ? '#ff9800'
                      : '#f44336',
                  textAlign: 'center',
                  margin: '8px 0',
                }}
              >
                {cs.compliancePercent}%
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ textAlign: 'center' }}
              >
                {cs.totalControls} controls
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Full control register */}
      <Typography variant="h6" className={classes.sectionTitle}>
        Full Control Register
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ref</TableCell>
              <TableCell>Control</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>WHO Reference</TableCell>
              <TableCell>Responsible</TableCell>
              <TableCell>Due Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {controls.map(ctrl => (
              <TableRow key={ctrl.id} hover>
                <TableCell>
                  <strong>{ctrl.controlRef}</strong>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {ctrl.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {AuditDomainLabel[ctrl.domain]}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    {ctrl.complianceStatus === 'compliant' && (
                      <CheckCircleIcon
                        style={{ color: '#4caf50', fontSize: 16 }}
                      />
                    )}
                    {ctrl.complianceStatus === 'partial' && (
                      <WarningIcon style={{ color: '#ff9800', fontSize: 16 }} />
                    )}
                    {ctrl.complianceStatus === 'non-compliant' && (
                      <ErrorIcon style={{ color: '#f44336', fontSize: 16 }} />
                    )}
                    {ctrl.complianceStatus === 'not-assessed' && (
                      <HelpIcon style={{ color: '#9e9e9e', fontSize: 16 }} />
                    )}
                    <Chip
                      label={ctrl.complianceStatus}
                      size="small"
                      style={{
                        backgroundColor:
                          ctrl.complianceStatus === 'compliant'
                            ? '#4caf50'
                            : ctrl.complianceStatus === 'partial'
                            ? '#ff9800'
                            : ctrl.complianceStatus === 'non-compliant'
                            ? '#f44336'
                            : '#9e9e9e',
                        color: '#fff',
                        fontSize: '0.65rem',
                        height: 18,
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={ctrl.priority}
                    size="small"
                    style={{
                      backgroundColor:
                        ctrl.priority === 'critical'
                          ? '#d32f2f'
                          : ctrl.priority === 'high'
                          ? '#f44336'
                          : ctrl.priority === 'medium'
                          ? '#ff9800'
                          : '#2196f3',
                      color: '#fff',
                      fontSize: '0.65rem',
                      height: 18,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{ctrl.whoReference}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {ctrl.responsibleParty}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {ctrl.dueDate || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
